/**
 * The order of the parts of an item's page: its checks, notes, details and files.
 *
 * Unless someone arranges it, what has the most comes first, by about how many lines each takes
 * on a phone (an open check counts a bit more: it's something to do). Arranged, it's kept on
 * the item ("just this one") or on a card for everything inside it, the nearest one winning.
 */
import type { ItemSnapshot } from './types'

export type PageSection = 'checks' | 'notes' | 'details' | 'files'
/** The usual order, for ties and anything new. */
export const PAGE_SECTIONS: readonly PageSection[] = ['checks', 'notes', 'details', 'files']

export interface PageAmounts {
  /** Checks still to do (rows without a box count too). */
  open: number
  done: number
  /** Characters of notes. */
  noteChars: number
  /** Details in the grid with a value. */
  details: number
  files: number
}

/** About how many lines of a phone each takes. */
export function pageWeights(a: PageAmounts): Record<PageSection, number> {
  return {
    checks: a.open * 1.5 + a.done * 0.25,
    notes: a.noteChars / 50,
    details: a.details * 1.2,
    files: a.files ? 1 + a.files / 2 : 0,
  }
}

/** Most first; ties (and empty ones) in the usual order. */
export function autoPageOrder(a: PageAmounts): PageSection[] {
  const w = pageWeights(a)
  return [...PAGE_SECTIONS].sort((x, y) => w[y] - w[x])
}

/** Known ones once each, in the given order, then any missing in the usual order. */
export function normalizePageOrder(list: readonly unknown[]): PageSection[] {
  const out = list.filter((k, i): k is PageSection => PAGE_SECTIONS.includes(k as PageSection) && list.indexOf(k) === i)
  return [...out, ...PAGE_SECTIONS.filter((k) => !out.includes(k))]
}

/** An arranged order, and where it's kept (the item's id, or the card's it comes from); null = automatic. */
export function chosenPageOrder(item: Pick<ItemSnapshot, 'id' | 'pageOrder'>, ancestors: Array<Pick<ItemSnapshot, 'id' | 'childPageOrder'>>): { order: PageSection[]; from: string } | null {
  if (item.pageOrder?.length) return { order: normalizePageOrder(item.pageOrder), from: item.id }
  for (const a of ancestors) if (a.childPageOrder?.length) return { order: normalizePageOrder(a.childPageOrder), from: a.id }
  return null
}
